import type { CodeableConcept, Coding } from '@medplum/fhirtypes'
import { OBSERVATION_INTERPRETATION_SYSTEM } from '#src/codeSystems.ts'
import type { ScoreBand } from '#src/observation-logic/derivations/scores.ts'
import { INSTRUMENT_TEMPLATES } from '#src/terminology/instruments/registry.ts'
import type {
  InstrumentScore,
  InstrumentTemplate,
} from '#src/terminology/instruments/types.ts'
import {
  type BandSet,
  type CodedBands,
  OBSERVATION_BANDS,
} from '#src/terminology/interpretations.ts'

function bandsKey(coding: Pick<Coding, 'system' | 'code'>): string {
  return `${coding.system ?? ''}|${coding.code ?? ''}`
}

function instrumentScoreBands(
  template: InstrumentTemplate,
  score: InstrumentScore
): CodedBands {
  const bandSet: BandSet = {
    id: score.rule.id,
    display: template.title,
    bands: score.bands,
  }
  if (template.reference) {
    bandSet.source = { citation: template.title, url: template.reference }
  }
  return { coding: score.rule.target, bandSets: [bandSet] }
}

const INSTRUMENT_SCORE_BANDS: readonly CodedBands[] =
  INSTRUMENT_TEMPLATES.flatMap((template) =>
    [template.total, ...(template.subtotals ?? [])].flatMap((score) =>
      score ? [instrumentScoreBands(template, score)] : []
    )
  )

const BANDS_BY_CODE = new Map(
  [...OBSERVATION_BANDS, ...INSTRUMENT_SCORE_BANDS].map(
    (entry) => [bandsKey(entry.coding), entry] as const
  )
)

// The cutoff sets a code can be banded by, in the order offered: an
// observation's from the terminology table, an instrument score's from its
// template.
export function bandSetsFor(
  coding: Pick<Coding, 'system' | 'code'>
): readonly BandSet[] {
  return BANDS_BY_CODE.get(bandsKey(coding))?.bandSets ?? []
}

// The chosen cutoff set for a code, else its default.
export function bandSetFor(
  coding: Pick<Coding, 'system' | 'code'>,
  chosenId?: string | null
): BandSet | undefined {
  const entry = BANDS_BY_CODE.get(bandsKey(coding))
  if (!entry) {
    return undefined
  }
  return (
    entry.bandSets.find((set) => set.id === chosenId) ??
    entry.bandSets.find((set) => set.id === entry.defaultId) ??
    entry.bandSets[0]
  )
}

// The bands for a code, when it has any, from the chosen or default set.
export function bandsFor(
  coding: Pick<Coding, 'system' | 'code'>,
  chosenId?: string | null
): readonly ScoreBand[] | undefined {
  return bandSetFor(coding, chosenId)?.bands
}

// The v3 ObservationInterpretation code for a band's severity.
const SEVERITY_INTERPRETATION: Readonly<
  Record<ScoreBand['severity'], { code: string; display: string }>
> = {
  green: { code: 'N', display: 'Normal' },
  yellow: { code: 'A', display: 'Abnormal' },
  orange: { code: 'A', display: 'Abnormal' },
  red: { code: 'AA', display: 'Critical abnormal' },
}

// `Observation.interpretation[0]` for a band, its display as text. A band
// coded in v3 ObservationInterpretation is its own coding; any other band
// (an instrument's) leads with the v3 code for its severity, for any FHIR
// reader.
export function bandToInterpretation(band: ScoreBand): CodeableConcept {
  return {
    coding:
      band.code.system === OBSERVATION_INTERPRETATION_SYSTEM
        ? [band.code]
        : [
            {
              system: OBSERVATION_INTERPRETATION_SYSTEM,
              ...SEVERITY_INTERPRETATION[band.severity],
            },
            band.code,
          ],
    text: band.display,
  }
}
